import { Component, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Accordion, AccordionPanel, AccordionHeader, AccordionContent } from '@openng/optimus-ui/accordion';
import { DropdownData, DynamicRouteHandlingService, rxResourceFixed, SvgIconsComponent } from '@zayan-erp/shared';
import { TemplateDocumentService } from '../../services/template-document.service';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TableModule, TableRowReorderEvent } from '@openng/optimus-ui/table';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import {
  TemplateDocument,
  TEMPLATE_TYPE_LABELS,
  TemplateType,
  TemplateDocumentSequenceDto
} from '../../models/template.models';
// import { TableRowReorderEvent } from '@openng/optimus-ui/table';
// import { TemplateDocumentSequenceDto } from '../../models/template.models';
import { ConfirmHelperService } from '@zayan-erp/shared';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../apps/cinari-erp-client/src/app/store/app.reducers';
import { selectProjects } from '@zayan-erp/stores';
import { FilesUtility } from '../../utils/files-utility';
import { Router, RouterOutlet } from '@angular/router';

interface ProjectTypeSection {
  type: TemplateType;
  label: string;
  templates: TemplateDocument[];
}

interface ProjectTemplates {
  projectId: string;
  projectName: string;
  projectCode: string;
  typeSections: ProjectTypeSection[];
  totalCount: number;
}

@Component({
  selector: 'lib-project-template',
  imports: [
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    NgbDropdownModule,
    TableModule,
    SvgIconsComponent,
    ConfirmDialogModule,
    FormsModule,
    RouterOutlet
  ],
  providers: [],
  templateUrl: './project-template.component.html'
})
export class ProjectTemplateComponent {
  #templateService = inject(TemplateDocumentService);
  #confirmService = inject(ConfirmHelperService);
  #destroyRef = inject(DestroyRef);
  #toastr = inject(ToastrService);
  #store = inject(Store<AppState>);
  #filesUtility = inject(FilesUtility);
  #route = inject(Router);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);

  reload = toSignal(this.#dynamicRouteHandling.reload$);
  projects = toSignal(this.#store.select(selectProjects), { initialValue: [] as DropdownData[] });

  templatesRxResource = rxResourceFixed<TemplateDocument[]>({
    params: () => ({ reload: this.reload() }),
    defaultValue: [],
    stream: ({}) => this.#templateService.readTemplateDocuments()
  });
  templates = computed(() => this.templatesRxResource.value() ?? []);

  readonly allTypes = Object.values(TemplateType);

  projectTemplates = computed<ProjectTemplates[]>(() => {
    return (this.projects() ?? []).map(project => {
      const projectTemplates = this.templates().filter(t => t.projectId === project.id);

      const typeSections: ProjectTypeSection[] = this.allTypes
        .map(type => {
          const sectionTemplates = projectTemplates
            .filter(t => t.documentType === type)
            .map((t, index) => ({ ...t, sequence: t.sequence ?? index + 1 }))
            .sort((a, b) => a.sequence - b.sequence);

          return {
            type,
            label: TEMPLATE_TYPE_LABELS[type],
            templates: sectionTemplates
          };
        })
        .filter(section => section.templates.length > 0);

      return {
        projectId: project.id,
        projectName: project.secondLabel ?? '',
        projectCode: project.firstLabel ?? '',
        typeSections,
        totalCount: projectTemplates.length
      };
    });
  });

  openAddProductModal(projectId: string) {
    this.#route.navigate([`/home-building/template-document/projects/${projectId}/add`]);
  }

  openReplaceModal(templateId: string) {
    this.#route.navigate([`/home-building/template-document/projects/${templateId}/replace`]);
  }

  deleteDocumentConfirmation(event: Event, templateId: string) {
    this.#confirmService
      .showModal({
        icon: 'confirm-icon',
        message: 'Do you want to delete this document? This action is permanent and cannot be undone.',
        header: 'Delete Document?',
        acceptLabel: 'Delete',
        acceptSeverity: 'danger',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.deleteTemplate(templateId);
        }
      });
  }

  deleteTemplate(templateId: string) {
    this.#templateService
      .toggleStatus(templateId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.templatesRxResource.reload();
      });
  }

  onReorder(event: TableRowReorderEvent, projectId: string, type: TemplateType) {
    const project = this.projectTemplates().find(p => p.projectId === projectId);
    const section = project?.typeSections.find(s => s.type === type);
    if (!section) {
      this.#toastr.error('Unable to find the section being reordered. Reordering is not saved!');
      return;
    }
    const index = event.dropIndex;
    if (index === undefined || !section.templates[index]) {
      this.#toastr.error('Unable to find which template is being reordered. Reordering is not saved!');
      return;
    }
    const targetTemplate = section.templates[index];
    const updateDto: TemplateDocumentSequenceDto = {
      id: targetTemplate.id,
      sequence: (event.dropIndex ?? 0) + 1
    };
    this.#templateService.reorderTemplateDocuments(updateDto).subscribe({
      next: () => this.templatesRxResource.reload()
    });
  }

  downloadDocument(template: TemplateDocument) {
    const url = template.s3Document?.directUrl;
    const filename = `${template.name}.${template.fileType}`;
    if (url && filename) {
      this.#filesUtility.downloadFileFromUrl(url, filename);
    } else {
      this.#toastr.error('File download URL or name is not available');
    }
  }

  openPreviewModal(template: TemplateDocument) {
    this.#route.navigate([`/home-building/template-document/projects/${template.id}`]);
  }
}
